import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

// Brand colors matching other animations
const CYAN_PRIMARY = '#00d4ff';
const CYAN_DIM = 'rgba(0, 212, 255, 0.3)';
const CYAN_GLOW = 'rgba(0, 212, 255, 0.4)';
const GRAY_DIM = '#3a3a4a';
const GRAY_TEXT = '#8a8a9a';
const BG_DARK = 'var(--pm-bg-base)';

interface EpisodeNotFoundProps {
  onBack?: () => void;
}

// Sparkle component for background decoration
const Sparkle: React.FC<{
  size: number;
  delay: number;
  style?: React.CSSProperties;
}> = ({ size, delay, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 12 12"
    style={{
      position: 'absolute',
      animation: `sparkle 3s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      ...style,
    }}
  >
    <path
      d="M6 0L6 12M0 6L12 6M1.75 1.75L10.25 10.25M10.25 1.75L1.75 10.25"
      stroke={CYAN_PRIMARY}
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
      opacity="0.6"
    />
  </svg>
);

const EpisodeNotFound: React.FC<EpisodeNotFoundProps> = ({ onBack }) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('../episodes');
    }
  };

  return (
    <>
      {/* Keyframe animations */}
      <style>{`
        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0.5) rotate(0deg);
          }
          50% {
            opacity: 0.7;
            transform: scale(1) rotate(180deg);
          }
        }

        @keyframes questionPulse {
          0%, 100% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
        }

        @keyframes searchBeam {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes ringRotate {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes iconGlow {
          0%, 45%, 55%, 100% {
            filter: drop-shadow(0 0 8px ${CYAN_DIM});
            opacity: 0.85;
          }
          50% {
            filter: drop-shadow(0 0 20px ${CYAN_GLOW}) drop-shadow(0 0 40px ${CYAN_DIM});
            opacity: 1;
          }
        }

        @keyframes particleDrift {
          0% {
            opacity: 0;
            transform: rotate(var(--start-angle)) translateY(-80px) scale(0);
          }
          20% {
            opacity: 0.8;
            transform: rotate(var(--start-angle)) translateY(-100px) scale(1);
          }
          80% {
            opacity: 0.4;
            transform: rotate(calc(var(--start-angle) + 30deg)) translateY(-120px) scale(0.5);
          }
          100% {
            opacity: 0;
            transform: rotate(calc(var(--start-angle) + 45deg)) translateY(-140px) scale(0);
          }
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes buttonSweep {
          0% {
            left: -100%;
          }
          50%, 100% {
            left: 100%;
          }
        }

        @keyframes curveFloat {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-10px) rotate(5deg);
            opacity: 0.5;
          }
        }

        @keyframes diskFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        .fade-in-1 {
          animation: fadeInUp 0.6s ease-out forwards;
          animation-delay: 0.1s;
          opacity: 0;
        }

        .fade-in-2 {
          animation: fadeInUp 0.6s ease-out forwards;
          animation-delay: 0.3s;
          opacity: 0;
        }

        .fade-in-3 {
          animation: fadeInUp 0.6s ease-out forwards;
          animation-delay: 0.5s;
          opacity: 0;
        }

        .fade-in-4 {
          animation: fadeInUp 0.6s ease-out forwards;
          animation-delay: 0.7s;
          opacity: 0;
        }

        .button-sweep {
          position: relative;
          overflow: hidden;
        }

        .button-sweep::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(0, 212, 255, 0.2),
            transparent
          );
          animation: buttonSweep 3s ease-in-out infinite;
        }
      `}</style>

      {/* Main container */}
      <div
        className="min-h-[60vh] flex items-center justify-center p-8 relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${BG_DARK} 0%, #000000 100%)` }}
      >
        {/* Top edge gradient fade */}
        <div
          className="absolute top-0 left-0 right-0 h-16 pointer-events-none"
          style={{
            background: `linear-gradient(180deg, hsl(var(--background)) 0%, transparent 100%)`,
          }}
        />

        {/* Bottom edge gradient fade */}
        <div
          className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
          style={{
            background: `linear-gradient(0deg, hsl(var(--background)) 0%, transparent 100%)`,
          }}
        />

        {/* Background decorative curves */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ opacity: 0.4 }}
        >
          <path
            d="M-100 200 Q 200 100 500 250 T 900 200"
            fill="none"
            stroke={CYAN_DIM}
            strokeWidth="1"
            style={{ animation: 'curveFloat 8s ease-in-out infinite' }}
          />
          <path
            d="M-50 350 Q 300 250 600 400 T 1000 350"
            fill="none"
            stroke={CYAN_DIM}
            strokeWidth="1"
            style={{ animation: 'curveFloat 10s ease-in-out infinite', animationDelay: '1s' }}
          />
          <path
            d="M0 500 Q 250 400 550 500 T 950 480"
            fill="none"
            stroke={CYAN_DIM}
            strokeWidth="1"
            style={{ animation: 'curveFloat 12s ease-in-out infinite', animationDelay: '2s' }}
          />
        </svg>

        {/* Background sparkles */}
        <Sparkle size={16} delay={0} style={{ top: '15%', left: '10%' }} />
        <Sparkle size={12} delay={1.5} style={{ top: '25%', right: '15%' }} />
        <Sparkle size={14} delay={0.8} style={{ bottom: '30%', left: '20%' }} />
        <Sparkle size={10} delay={2.2} style={{ bottom: '20%', right: '25%' }} />
        <Sparkle size={18} delay={0.5} style={{ top: '40%', left: '5%' }} />
        <Sparkle size={11} delay={1.8} style={{ top: '60%', right: '8%' }} />

        {/* Content */}
        <div className="text-center relative z-10 max-w-md">
          {/* Icon container with animations */}
          <div className="fade-in-1 relative mx-auto mb-8" style={{ width: '200px', height: '200px' }}>
            {/* Rotating ring */}
            <svg
              className="absolute inset-0 w-full h-full"
              style={{ animation: 'ringRotate 8s linear infinite' }}
            >
              <circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke={GRAY_DIM}
                strokeWidth="2"
                strokeDasharray="4 8"
              />
              {/* Cyan accent segment */}
              <circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke={CYAN_PRIMARY}
                strokeWidth="3"
                strokeDasharray="60 506"
                strokeLinecap="round"
                style={{ filter: `drop-shadow(0 0 6px ${CYAN_GLOW})` }}
              />
            </svg>

            {/* Searchlight beam */}
            <div
              className="absolute inset-0"
              style={{ animation: 'searchBeam 4s linear infinite' }}
            >
              <svg className="w-full h-full">
                <defs>
                  <linearGradient id="beamGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={CYAN_PRIMARY} stopOpacity="0.5" />
                    <stop offset="100%" stopColor={CYAN_PRIMARY} stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M100 100 L100 10 A90 90 0 0 1 170 35 Z"
                  fill="url(#beamGradient)"
                  style={{ filter: `blur(2px)` }}
                />
              </svg>
            </div>

            {/* Floating particles in beam path */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <div
                key={angle}
                style={{
                  position: 'absolute',
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  backgroundColor: CYAN_PRIMARY,
                  left: '50%',
                  top: '50%',
                  marginLeft: '-2px',
                  marginTop: '-2px',
                  animation: `particleDrift 4s ease-in-out infinite`,
                  animationDelay: `${i * 0.5}s`,
                  ['--start-angle' as string]: `${angle}deg`,
                  opacity: 0,
                  boxShadow: `0 0 6px ${CYAN_PRIMARY}`,
                }}
              />
            ))}

            {/* Floppy disk icon */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                animation: 'iconGlow 4s ease-in-out infinite, diskFloat 3s ease-in-out infinite',
              }}
            >
              <svg
                width="100"
                height="100"
                viewBox="0 0 100 100"
                fill="none"
              >
                {/* Disk body */}
                <rect
                  x="10"
                  y="10"
                  width="80"
                  height="80"
                  rx="6"
                  fill={BG_DARK}
                  stroke={CYAN_PRIMARY}
                  strokeWidth="2"
                />

                {/* Metal slider area (top) */}
                <rect
                  x="25"
                  y="10"
                  width="50"
                  height="25"
                  fill={GRAY_DIM}
                  stroke={CYAN_PRIMARY}
                  strokeWidth="1"
                />
                {/* Slider opening */}
                <rect
                  x="55"
                  y="14"
                  width="16"
                  height="17"
                  fill={BG_DARK}
                  stroke={CYAN_DIM}
                  strokeWidth="1"
                />

                {/* Label area with question mark */}
                <rect
                  x="20"
                  y="45"
                  width="60"
                  height="35"
                  rx="2"
                  fill="rgba(0, 212, 255, 0.1)"
                  stroke={CYAN_DIM}
                  strokeWidth="1"
                />

                {/* Pulsing question mark */}
                <text
                  x="50"
                  y="72"
                  textAnchor="middle"
                  fontSize="28"
                  fontWeight="bold"
                  fill={CYAN_PRIMARY}
                  style={{
                    animation: 'questionPulse 2s ease-in-out infinite',
                    transformOrigin: '50px 65px',
                  }}
                >
                  ?
                </text>

                {/* Write-protect notch (bottom left) */}
                <rect
                  x="10"
                  y="75"
                  width="8"
                  height="15"
                  fill={BG_DARK}
                  stroke={CYAN_DIM}
                  strokeWidth="1"
                />

                {/* Corner cut (top right) */}
                <path
                  d="M90 10 L90 25 L75 10 Z"
                  fill={GRAY_DIM}
                  stroke={CYAN_DIM}
                  strokeWidth="1"
                />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h1
            className="fade-in-2 text-2xl font-medium mb-3"
            style={{ color: '#ffffff' }}
          >
            Episode Not Found
          </h1>

          {/* Description */}
          <p
            className="fade-in-3 mb-8"
            style={{ color: GRAY_TEXT }}
          >
            This episode may have been deleted or you don't have permission to view it.
          </p>

          {/* Button */}
          <div className="fade-in-4">
            <Button
              onClick={handleBack}
              className="button-sweep"
              style={{
                backgroundColor: CYAN_PRIMARY,
                color: BG_DARK,
                border: 'none',
                fontWeight: 600,
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Episodes
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EpisodeNotFound;
